import type { AnswersByQuestionId, QuestionRoutingRules } from '@/modules/routing'
import { evaluateRoutingPath, resolveRuleDestination } from '@/modules/routing'
import { questionIndexInDefinition } from './parse-survey-definition'
import type { SurveyDefinition } from './survey-question.types'
import { toRoutingQuestions } from './survey-routing-preview'

/** Falls back to the current question when the server visit stack is empty. */
function resolveEffectiveRoutingPath(
  routingPath: string[],
  currentQuestionId: string | null,
): string[] {
  if (routingPath.length > 0) {
    return routingPath
  }
  if (currentQuestionId) {
    return [currentQuestionId]
  }
  return []
}

/** Preserves first occurrence order; drops duplicate ids from stale server stacks. */
function dedupePreservingOrder(ids: readonly string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const id of ids) {
    if (!seen.has(id)) {
      seen.add(id)
      result.push(id)
    }
  }
  return result
}

/** Visited stack reconciled with the active question (stale server routingPath). */
function effectiveVisitedPath(
  routingPath: string[],
  currentQuestionId: string | null,
): string[] {
  const deduped = dedupePreservingOrder(routingPath)
  if (!currentQuestionId || deduped.length === 0) {
    return deduped
  }
  if (deduped.includes(currentQuestionId)) {
    return deduped
  }
  return [...deduped, currentQuestionId]
}


function resolveRoutingDestinations(
  routingRules: QuestionRoutingRules[],
): Array<{ sourceId: string; destId: string }> {
  const jumps: Array<{ sourceId: string; destId: string }> = []
  for (const rule of routingRules) {
    const ruleMatches = [...(rule.branchingRules ?? []), ...(rule.skipRules ?? [])]
    for (const match of ruleMatches) {
      const dest = resolveRuleDestination(match)
      const destId = dest?.destinationQuestionId
      if (destId) {
        jumps.push({ sourceId: rule.questionId, destId })
      }
    }
  }
  return jumps
}

/** True when any rule on `questionId` can route to end of instrument. */
function questionCanEndInstrument(
  routingRules: QuestionRoutingRules[],
  questionId: string,
): boolean {
  const rule = routingRules.find((r) => r.questionId === questionId)
  if (!rule) return false
  const ruleMatches = [...(rule.branchingRules ?? []), ...(rule.skipRules ?? [])]
  return ruleMatches.some(
    (match) => resolveRuleDestination(match)?.endOfInstrument === true,
  )
}

/** Stops linear tail projection when routing ends at the last visited question. */
function shouldProjectLinearTail(
  definition: SurveyDefinition,
  visitedPath: string[],
  routingRules: QuestionRoutingRules[],
  lastId: string,
): boolean {
  const lastIdx = questionIndexInDefinition(definition, lastId)
  if (lastIdx >= definition.questions.length - 1) {
    return false
  }

  if (questionCanEndInstrument(routingRules, lastId)) {
    return false
  }

  return true
}

/** Collapses stale visit stacks only when saved answers confirm a routing jump skipped intermediates. */
export function compressRoutingVisitStack(
  routingPath: string[],
  routingRules: QuestionRoutingRules[],
  definition: SurveyDefinition,
  answersByQuestionId?: AnswersByQuestionId,
): string[] {
  if (!answersByQuestionId || Object.keys(answersByQuestionId).length === 0) {
    return [...routingPath]
  }

  let path = [...routingPath]
  const questions = toRoutingQuestions(definition.questions)

  for (const { sourceId, destId } of resolveRoutingDestinations(routingRules)) {
    const srcIdx = path.indexOf(sourceId)
    const destIdx = path.indexOf(destId)
    if (srcIdx < 0 || destIdx <= srcIdx + 1) {
      continue
    }

    const sourceAnswer = answersByQuestionId[sourceId]
    if (sourceAnswer == null) {
      continue
    }

    const step = evaluateRoutingPath(
      questions,
      routingRules,
      answersByQuestionId,
      sourceId,
    )
    if (step.nextQuestionId !== destId) {
      continue
    }

    const intermediates = path.slice(srcIdx + 1, destIdx)
    const skippedIds = new Set(step.skippedQuestionIds)
    const routingJumpSkippedIntermediates =
      intermediates.length > 0 && intermediates.every((id) => skippedIds.has(id))

    if (routingJumpSkippedIntermediates) {
      path = [...path.slice(0, srcIdx + 1), ...path.slice(destIdx)]
    }
  }

  return path
}

function compressStaleRoutingPath(
  routingPath: string[],
  routingRules: QuestionRoutingRules[],
  definition: SurveyDefinition,
  answersByQuestionId?: AnswersByQuestionId,
): string[] {
  return compressRoutingVisitStack(
    routingPath,
    routingRules,
    definition,
    answersByQuestionId,
  )
}

type ProjectedActivePathOptions = {
  /** When true, extend linear successors even if the last question has an end-of-instrument rule. */
  forceLinearTail?: boolean
}

/** Projects the routed tail by walking definition-order linear successors. */
function buildProjectedActivePath(
  definition: SurveyDefinition,
  visitedPath: string[],
  routingRules?: QuestionRoutingRules[],
  options?: ProjectedActivePathOptions,
): string[] {
  if (visitedPath.length === 0) {
    return definition.questions.map((q) => q.id)
  }

  const path = [...visitedPath]
  const lastElement = path.at(-1)
  if (lastElement === undefined) {
    return definition.questions.map((q) => q.id)
  }
  let lastId = lastElement

  while (true) {
    if (
      !options?.forceLinearTail &&
      routingRules?.length &&
      !shouldProjectLinearTail(definition, path, routingRules, lastId)
    ) {
      break
    }

    const lastIdx = questionIndexInDefinition(definition, lastId)
    if (lastIdx >= definition.questions.length - 1) {
      break
    }
    const nextId = definition.questions[lastIdx + 1]?.id
    if (!nextId) {
      break
    }
    path.push(nextId)
    lastId = nextId
  }

  return path
}

type RoutedPathProjectionParams = {
  routingPath: string[]
  currentQuestionId: string | null
  definition: SurveyDefinition
  hasRoutingRules: boolean
  routingRules?: QuestionRoutingRules[]
  answersByQuestionId?: AnswersByQuestionId
  endsAtCurrentQuestion?: boolean
}

/** Visited stack + projected tail, honoring the current draft answer when routable. */
function resolveProjectedRoutedPathIds(params: RoutedPathProjectionParams): string[] {
  const visited = reconcileActiveVisitedPath(
    params.definition,
    params.routingPath,
    params.currentQuestionId,
    params.hasRoutingRules,
    params.routingRules,
    params.answersByQuestionId,
  )

  if (params.endsAtCurrentQuestion) {
    return visited
  }

  const currentQuestionId = params.currentQuestionId
  const routingRules = params.routingRules
  const draftAnswer =
    currentQuestionId === null
      ? undefined
      : params.answersByQuestionId?.[currentQuestionId]

  if (routingRules?.length && currentQuestionId && draftAnswer != null) {
    const questions = toRoutingQuestions(params.definition.questions)
    const step = evaluateRoutingPath(
      questions,
      routingRules,
      params.answersByQuestionId ?? {},
      currentQuestionId,
    )
    const baseVisited = effectiveVisitedPath(visited, currentQuestionId)

    if (step.endOfInstrument) {
      return baseVisited
    }

    if (step.nextQuestionId) {
      const linearNextId =
        params.definition.questions[
          questionIndexInDefinition(params.definition, currentQuestionId) + 1
        ]?.id
      const currentPos = baseVisited.indexOf(currentQuestionId)
      const prefix =
        currentPos >= 0
          ? baseVisited.slice(0, currentPos + 1)
          : [...baseVisited, currentQuestionId]

      if (step.nextQuestionId !== linearNextId) {
        return buildProjectedActivePath(
          params.definition,
          [...prefix, step.nextQuestionId],
          routingRules,
        )
      }

      return buildProjectedActivePath(params.definition, baseVisited, routingRules, {
        forceLinearTail: true,
      })
    }
  }

  return buildProjectedActivePath(params.definition, visited, routingRules)
}

function reconcileActiveVisitedPath(
  definition: SurveyDefinition,
  routingPath: string[],
  currentQuestionId: string | null,
  hasRoutingRules: boolean,
  routingRules?: QuestionRoutingRules[],
  answersByQuestionId?: AnswersByQuestionId,
): string[] {
  if (routingRules?.length) {
    const compressed = compressStaleRoutingPath(
      routingPath,
      routingRules,
      definition,
      answersByQuestionId,
    )
    return effectiveVisitedPath(compressed, currentQuestionId)
  }

  return effectiveVisitedPath(routingPath, currentQuestionId)
}

/** Question ids on the active routed path (visited stack + projected linear tail). */
export function resolveActiveRoutedQuestionIds(params: {
  routingPath: string[]
  currentQuestionId: string | null
  definition: SurveyDefinition | null
  hasRoutingRules: boolean
  routingRules?: QuestionRoutingRules[]
  answersByQuestionId?: AnswersByQuestionId
  endsAtCurrentQuestion?: boolean
}): string[] {
  if (!params.definition) {
    return []
  }

  if (params.hasRoutingRules) {
    const routingPath = resolveEffectiveRoutingPath(
      params.routingPath,
      params.currentQuestionId,
    )

    if (routingPath.length > 0) {
      return resolveProjectedRoutedPathIds({
        routingPath,
        currentQuestionId: params.currentQuestionId,
        definition: params.definition,
        hasRoutingRules: params.hasRoutingRules,
        routingRules: params.routingRules,
        answersByQuestionId: params.answersByQuestionId,
        endsAtCurrentQuestion: params.endsAtCurrentQuestion,
      })
    }
  }

  if (params.currentQuestionId) {
    const startIdx = questionIndexInDefinition(params.definition, params.currentQuestionId)
    return params.definition.questions.slice(startIdx).map((q) => q.id)
  }

  return params.definition.questions.map((q) => q.id)
}

type RoutingProgressParams = {
  routingPath: string[]
  currentQuestionId: string | null
  definition: SurveyDefinition
  hasRoutingRules: boolean
  routingRules?: QuestionRoutingRules[]
  answersByQuestionId?: AnswersByQuestionId
  endsAtCurrentQuestion?: boolean
}

function computeRoutedQuestionIndex(
  params: RoutingProgressParams,
  projectedPath: string[],
  reconciledVisited: string[],
): number {
  if (params.currentQuestionId == null) {
    return reconciledVisited.length
  }

  const hasRoutableDraftOnCurrent =
    params.answersByQuestionId?.[params.currentQuestionId] != null

  if (hasRoutableDraftOnCurrent && !params.endsAtCurrentQuestion) {
    const posInProjected = projectedPath.lastIndexOf(params.currentQuestionId)
    return posInProjected >= 0 ? posInProjected + 1 : Math.max(projectedPath.length, 1)
  }

  const posInVisited = reconciledVisited.lastIndexOf(params.currentQuestionId)
  return posInVisited >= 0 ? posInVisited + 1 : Math.max(reconciledVisited.length, 1)
}

function computeRoutedTotalQuestions(
  questionIndex: number,
  projectedPath: string[],
  endsAtCurrentQuestion?: boolean,
): number {
  if (endsAtCurrentQuestion) {
    return Math.max(questionIndex, 1)
  }
  return projectedPath.length || 1
}

function resolveProgressFromRoutedPath(
  params: RoutingProgressParams,
  routingPath: string[],
): { questionIndex: number; totalQuestions: number } {
  const projectedPath = resolveProjectedRoutedPathIds({
    routingPath,
    currentQuestionId: params.currentQuestionId,
    definition: params.definition,
    hasRoutingRules: params.hasRoutingRules,
    routingRules: params.routingRules,
    answersByQuestionId: params.answersByQuestionId,
    endsAtCurrentQuestion: params.endsAtCurrentQuestion,
  })
  const reconciledVisited = reconcileActiveVisitedPath(
    params.definition,
    routingPath,
    params.currentQuestionId,
    params.hasRoutingRules,
    params.routingRules,
    params.answersByQuestionId,
  )
  const questionIndex = computeRoutedQuestionIndex(
    params,
    projectedPath,
    reconciledVisited,
  )
  const totalQuestions = computeRoutedTotalQuestions(
    questionIndex,
    projectedPath,
    params.endsAtCurrentQuestion,
  )
  return {
    questionIndex: Math.max(1, questionIndex),
    totalQuestions,
  }
}

export function resolveRoutingAwareProgress(params: {
  routingPath: string[]
  currentQuestionId: string | null
  definition: SurveyDefinition | null
  hasRoutingRules: boolean
  routingRules?: QuestionRoutingRules[]
  answersByQuestionId?: AnswersByQuestionId
  /** When true, current question is the routed-path terminus (e.g. end-of-instrument after answer). */
  endsAtCurrentQuestion?: boolean
}): { questionIndex: number; totalQuestions: number } {
  const linearIndex =
    params.definition && params.currentQuestionId
      ? questionIndexInDefinition(params.definition, params.currentQuestionId) + 1
      : 1
  const linearTotal = params.definition?.questions.length ?? 0

  if (params.hasRoutingRules && params.definition) {
    const routingPath = resolveEffectiveRoutingPath(
      params.routingPath,
      params.currentQuestionId,
    )

    if (routingPath.length > 0) {
      return resolveProgressFromRoutedPath(
        {
          routingPath: params.routingPath,
          currentQuestionId: params.currentQuestionId,
          definition: params.definition,
          hasRoutingRules: params.hasRoutingRules,
          routingRules: params.routingRules,
          answersByQuestionId: params.answersByQuestionId,
          endsAtCurrentQuestion: params.endsAtCurrentQuestion,
        },
        routingPath,
      )
    }
  }

  return {
    questionIndex: linearIndex,
    totalQuestions: linearTotal || 1,
  }
}
